/**
 * AgentSettings Page — the per-agent control surface (SPEC §2.6).
 *
 * One uniform settings pattern for all 10 agents: on/off, autonomy level,
 * exploration %, budget caps, safety caps, connector scope. Platform floors
 * (spend always Tier-2, safety-cap limits, the anti-calcification
 * exploration minimum) are rendered as FIXED PLATFORM POLICY — read-only
 * text, deliberately never toggles. The server enforces them regardless.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  InlineStack,
  Layout,
  Page,
  Select,
  Spinner,
  Text,
  TextField,
  Tooltip,
} from '@shopify/polaris';
import type {
  AgentSettings as AgentSettingsType,
  AgentSettingsUpdate,
  PlatformPolicy,
} from '../types/agentSettings';
import {
  AGENT_DEPENDENCY_ORDER,
  getAutonomyLevelLabel,
} from '../types/agentSettings';
import {
  listAgentSettings,
  listSettingsAudit,
  updateAgentSettings,
} from '../services/agentSettingsApi';
import type { SettingsAuditEntry } from '../services/agentSettingsApi';

/**
 * Marks a control that is stored and validated today but not yet consumed by
 * the runtime — so the UI never implies live enforcement it can't back.
 * These knobs get wired when the engine unfreezes (per-domain picking) or
 * Track 3 lands (messaging connectors / budget checks in the pipeline).
 */
function PendingBadge() {
  return (
    <Tooltip content="Stored and validated now, but not yet enforced by the runtime. This control takes effect in a later milestone.">
      <Badge tone="attention" progress="incomplete">
        Not yet enforced
      </Badge>
    </Tooltip>
  );
}

interface AgentCardProps {
  agent: AgentSettingsType;
  policy: PlatformPolicy;
  onSave: (domain: AgentSettingsType['domain'], update: AgentSettingsUpdate) => Promise<void>;
  isSaving: boolean;
}

function AgentCard({ agent, policy, onSave, isSaving }: AgentCardProps) {
  const [autonomyLevel, setAutonomyLevel] = useState(agent.autonomy_level);
  const [exploration, setExploration] = useState(
    String(Math.round(agent.exploration_fraction * 100))
  );
  const [dailyBudget, setDailyBudget] = useState(
    agent.budget_caps.daily_usd != null ? String(agent.budget_caps.daily_usd) : ''
  );
  const [monthlyBudget, setMonthlyBudget] = useState(
    agent.budget_caps.monthly_usd != null ? String(agent.budget_caps.monthly_usd) : ''
  );
  const [safetyCaps, setSafetyCaps] = useState<Record<string, string>>(
    Object.fromEntries(
      Object.entries(agent.safety_caps).map(([k, v]) => [k, v != null ? String(v) : ''])
    )
  );
  const [connectors, setConnectors] = useState<string[]>(agent.allowed_platforms);

  if (!agent.configured) {
    return (
      <Card>
        <BlockStack gap="200">
          <InlineStack gap="200" blockAlign="center">
            <Text as="h3" variant="headingSm">
              {agent.display_name}
            </Text>
            <Badge>Not yet available</Badge>
          </InlineStack>
          <Text as="p" variant="bodySm" tone="subdued">
            This agent has not been rolled out by the platform yet. Agents are
            enabled in dependency order (Product Marketing first).
          </Text>
        </BlockStack>
      </Card>
    );
  }

  const handleSave = () => {
    const update: AgentSettingsUpdate = {
      autonomy_level: autonomyLevel,
      exploration_fraction: Number(exploration) / 100,
      budget_caps: {
        daily_usd: dailyBudget === '' ? null : Number(dailyBudget),
        monthly_usd: monthlyBudget === '' ? null : Number(monthlyBudget),
      },
      safety_caps: Object.fromEntries(
        Object.entries(safetyCaps)
          .filter(([, v]) => v !== '')
          .map(([k, v]) => [k, Number(v)])
      ),
      allowed_platforms: connectors,
    };
    onSave(agent.domain, update);
  };

  const handleToggleEnabled = () => {
    onSave(agent.domain, { enabled: !agent.enabled });
  };

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Text as="h3" variant="headingSm">
              {agent.display_name}
            </Text>
            <Badge tone={agent.enabled ? 'success' : undefined}>
              {agent.enabled ? 'On' : 'Off'}
            </Badge>
            {agent.source === 'tenant' && (
              <Badge tone="info">Customized</Badge>
            )}
          </InlineStack>
          <Button
            onClick={handleToggleEnabled}
            loading={isSaving}
            variant={agent.enabled ? 'secondary' : 'primary'}
          >
            {agent.enabled ? 'Turn off' : 'Turn on'}
          </Button>
        </InlineStack>

        {agent.owned_questions.length > 0 && (
          <Text as="p" variant="bodySm" tone="subdued">
            Owns: {agent.owned_questions[0]}
            {agent.owned_questions.length > 1 &&
              ` (+${agent.owned_questions.length - 1} more)`}
          </Text>
        )}

        <Divider />

        <InlineStack gap="200" blockAlign="center">
          <Select
            label="Autonomy level"
            options={policy.autonomy_levels.map(level => ({
              label: getAutonomyLevelLabel(level),
              value: level,
            }))}
            value={autonomyLevel}
            onChange={v => setAutonomyLevel(v as AgentSettingsType['autonomy_level'])}
            helpText="Spend and net-new messaging always require human approval regardless of this setting. Stored now; per-agent gating is applied once the reasoning worker consumes it — today the tenant-level autonomy switch governs auto-approval."
          />
          <PendingBadge />
        </InlineStack>

        <InlineStack gap="200" blockAlign="center">
          <TextField
            label="Exploration effort (%)"
            type="number"
            value={exploration}
            onChange={setExploration}
            autoComplete="off"
            min={policy.exploration_bounds.min * 100}
            max={policy.exploration_bounds.max * 100}
            helpText={`Between ${policy.exploration_bounds.min * 100}% and ${
              policy.exploration_bounds.max * 100
            }% — exploration can be tuned down, never to zero (anti-calcification). The reasoning cycle reserves this share of each batch to challenge current winners; the batch uses the highest dial among enabled agents.`}
          />
        </InlineStack>

        <BlockStack gap="100">
          <InlineStack gap="400">
            <TextField
              label="Daily experiment budget (USD)"
              type="number"
              value={dailyBudget}
              onChange={setDailyBudget}
              autoComplete="off"
              placeholder="No cap"
              min={0}
            />
            <TextField
              label="Monthly experiment budget (USD)"
              type="number"
              value={monthlyBudget}
              onChange={setMonthlyBudget}
              autoComplete="off"
              placeholder="No cap"
              min={0}
            />
          </InlineStack>
          <Text as="p" variant="bodySm" tone="subdued">
            Enforced at approval: absolute budget changes by this agent are
            summed per day/month against these caps, and a breach (or a
            percentage change that cannot be costed) is refused with the
            reason shown.
          </Text>
        </BlockStack>

        <BlockStack gap="200">
          <Text as="h4" variant="headingXs">
            Safety caps
          </Text>
          {Object.entries(policy.safety_cap_floors).map(([key, floor]) => (
            <InlineStack key={key} gap="200" blockAlign="center">
              <TextField
                label={floor.label}
                type="number"
                value={safetyCaps[key] ?? ''}
                onChange={v => setSafetyCaps(prev => ({ ...prev, [key]: v }))}
                autoComplete="off"
                helpText={
                  floor.direction === 'max'
                    ? `Platform limit: at most ${floor.platform_limit}. You can tighten it, never loosen it.`
                    : `Platform minimum: at least ${floor.platform_limit}. You can tighten it, never loosen it.`
                }
              />
              {key !== 'max_sends_per_contact_per_week' && <PendingBadge />}
            </InlineStack>
          ))}
          <Text as="p" variant="bodySm" tone="subdued">
            The weekly per-contact send cap is enforced on every messaging
            execution (the strictest enabled agent's value binds, sharing one
            weekly budget per contact). Deliverability and list-health scores
            are enforced once bounce/complaint ingestion lands with the email
            write path.
          </Text>
        </BlockStack>

        {agent.system_allowed_platforms.length > 0 && (
          <BlockStack gap="200">
            <Text as="h4" variant="headingXs">
              Connector scope
            </Text>
            {agent.system_allowed_platforms.map(connector => (
              <Checkbox
                key={connector}
                label={connector}
                checked={connectors.includes(connector)}
                onChange={checked =>
                  setConnectors(prev =>
                    checked
                      ? [...prev, connector]
                      : prev.filter(c => c !== connector)
                  )
                }
              />
            ))}
            <Text as="p" variant="bodySm" tone="subdued">
              Only connectors granted by the platform definition are listed —
              scope can be narrowed here, never widened.
            </Text>
          </BlockStack>
        )}

        <InlineStack align="end">
          <Button variant="primary" onClick={handleSave} loading={isSaving}>
            Save settings
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

const SURFACE_LABELS: Record<string, string> = {
  agent_settings: 'Agent settings',
  enablement_master: 'Autonomy master switch',
  llm_byok: 'LLM key',
  llm_tier_config: 'LLM tier models',
};

function formatChange(
  field: string,
  change: { old: unknown; new: unknown }
): string {
  return `${field}: ${JSON.stringify(change.old)} → ${JSON.stringify(change.new)}`;
}

function SettingsHistoryCard({ entries }: { entries: SettingsAuditEntry[] }) {
  return (
    <Card>
      <BlockStack gap="200">
        <Text as="h3" variant="headingSm">
          Recent settings changes
        </Text>
        {entries.length === 0 && (
          <Text as="p" variant="bodySm" tone="subdued">
            No settings have been changed yet. Every change to agent knobs,
            autonomy, or LLM configuration is recorded here.
          </Text>
        )}
        {entries.map(entry => (
          <Box key={entry.id} paddingBlockStart="100" paddingBlockEnd="100">
            <BlockStack gap="050">
              <InlineStack gap="200" blockAlign="center">
                <Badge>
                  {SURFACE_LABELS[entry.surface] || entry.surface}
                </Badge>
                <Text as="span" variant="bodySm" fontWeight="semibold">
                  {entry.object_key.replace(/_/g, ' ')}
                </Text>
                <Text as="span" variant="bodySm" tone="subdued">
                  {entry.event}
                  {entry.changed_by ? ` by ${entry.changed_by}` : ''}
                  {entry.created_at
                    ? ` · ${new Date(entry.created_at).toLocaleString()}`
                    : ''}
                </Text>
              </InlineStack>
              {Object.entries(entry.changes).map(([field, change]) => (
                <Text key={field} as="p" variant="bodySm" tone="subdued">
                  {formatChange(field, change)}
                </Text>
              ))}
            </BlockStack>
          </Box>
        ))}
      </BlockStack>
    </Card>
  );
}

function PlatformPolicyCard({ policy }: { policy: PlatformPolicy }) {
  return (
    <Card>
      <BlockStack gap="200">
        <Text as="h3" variant="headingSm">
          Platform policy (fixed)
        </Text>
        <Text as="p" variant="bodySm" tone="subdued">
          These guarantees are the integrity of the product. They are enforced
          in code and cannot be changed by any setting on this page.
        </Text>
        <Box paddingBlockStart="100">
          <BlockStack gap="150">
            <Text as="p" variant="bodySm">
              • {policy.tier_floors.description}
            </Text>
            <Text as="p" variant="bodySm">
              • Always human-gated (Tier 2):{' '}
              {policy.tier_floors.tier2_floor_action_types.join(', ')}
            </Text>
            <Text as="p" variant="bodySm">
              • Tier-1 eligible today:{' '}
              {policy.tier_floors.tier1_eligible_action_types.join(', ') ||
                'none'}
            </Text>
            <Text as="p" variant="bodySm">
              • {policy.exploration_bounds.description}
            </Text>
            <Text as="p" variant="bodySm">
              • Statistical honesty, the 5-month retest floor, tenant
              isolation, and attribution honesty are engine invariants — they
              have no settings.
            </Text>
          </BlockStack>
        </Box>
      </BlockStack>
    </Card>
  );
}

export function AgentSettings() {
  const [agents, setAgents] = useState<AgentSettingsType[]>([]);
  const [policy, setPolicy] = useState<PlatformPolicy | null>(null);
  const [history, setHistory] = useState<SettingsAuditEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingDomains, setSavingDomains] = useState<Set<string>>(new Set());

  const fetchHistory = useCallback(async () => {
    try {
      const audit = await listSettingsAudit(20);
      setHistory(audit.entries);
    } catch (err) {
      // History is auxiliary - don't block the settings page on it.
      console.error('Failed to load settings history:', err);
    }
  }, []);

  const fetchAgents = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await listAgentSettings();
      setAgents(response.agents);
      setPolicy(response.platform_policy);
      fetchHistory();
    } catch (err) {
      console.error('Failed to load agent settings:', err);
      setError('Failed to load agent settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [fetchHistory]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleSave = async (
    domain: AgentSettingsType['domain'],
    update: AgentSettingsUpdate
  ) => {
    setSavingDomains(prev => new Set(prev).add(domain));
    setError(null);
    try {
      const updated = await updateAgentSettings(domain, update);
      setAgents(prev => prev.map(a => (a.domain === domain ? updated : a)));
      fetchHistory();
    } catch (err) {
      const detail =
        (err as Error & { detail?: string }).detail || 'Please try again.';
      setError(`Could not save settings: ${detail}`);
    } finally {
      setSavingDomains(prev => {
        const next = new Set(prev);
        next.delete(domain);
        return next;
      });
    }
  };

  const ordered = [...agents].sort(
    (a, b) =>
      AGENT_DEPENDENCY_ORDER.indexOf(a.domain) -
      AGENT_DEPENDENCY_ORDER.indexOf(b.domain)
  );

  return (
    <Page
      title="Team settings"
      subtitle="Tune how bold or cautious each member of your marketing team is. Every setting stays inside the platform's guarantees."
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {error && (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                {error}
              </Banner>
            )}

            {isLoading && (
              <InlineStack align="center">
                <Spinner size="large" />
              </InlineStack>
            )}

            {!isLoading && policy && <PlatformPolicyCard policy={policy} />}

            {!isLoading &&
              policy &&
              ordered.map(agent => (
                <AgentCard
                  key={agent.domain}
                  agent={agent}
                  policy={policy}
                  onSave={handleSave}
                  isSaving={savingDomains.has(agent.domain)}
                />
              ))}

            {!isLoading && policy && (
              <SettingsHistoryCard entries={history} />
            )}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default AgentSettings;
